<script setup lang="ts">
import { isCalm } from '../util/calm';
/**
 * ConfettiCelebration.vue
 * Full-screen confetti burst for milestone celebrations.
 * Also supports a 'sparkle' mode — calcium-imaging–inspired tiny twinkling stars.
 * Uses a canvas for smooth 60fps animation with physics-based particles.
 *
 * Usage: call trigger() or trigger('gold') to fire a confetti burst.
 *        call sparkle() to fire a calcium-imaging shimmer effect.
 * Exposed via defineExpose so parent can call confettiRef.trigger() or .sparkle().
 */
import { ref, onMounted, onUnmounted } from 'vue';
import { isMobileRef } from '../util/mobile';

/** inline: stay where it is placed instead of covering the whole page, so a
 *  panel can sit in front of it (the Champions cards, Ames 2026-10-26). */
const props = defineProps<{ inline?: boolean }>();
const canvasRef = ref<HTMLCanvasElement | null>(null);
let ctx: CanvasRenderingContext2D | null = null;
let animFrame = 0;
let particles: Particle[] = [];
let sparkles: Sparkle[] = [];
/** Sparkle-only runs render UNDER the mobile welcome sheet (z 10400 vs its
 *  10500): the shimmer is scenery the sheet floats over, never dots on top
 *  of readable UI (Amy 2026-08-24). Confetti bursts flip back on top. */
const underUI = ref(false);
/** Mobile shimmer opens behind a dark particle veil that dissolves away,
 *  so the app is REVEALED through the sparkles instead of overlapped.
 *  veilT runs 0→1; opacity follows a cosine ease so the dissolve flows
 *  smoothly out instead of dropping fast then crawling (Amy 2026-08-24). */
let veilT = -1;            // -1 = no veil
const VEIL_MAX = 0.85;
const VEIL_STEP = 1 / 54;  // ~0.9s at 60fps

// Color palettes for different milestone types
const PALETTES: Record<string, string[]> = {
  default: ['#00c8ff', '#CE93D8', '#7fff88', '#ffd700', '#ff6b8a', '#4a9eff', '#ff9500'],
  gold:    ['#ffd700', '#ffea70', '#ffa500', '#fff4b0', '#e6c200', '#ffcc00', '#fff8dc'],
  purple:  ['#CE93D8', '#AB47BC', '#9C27B0', '#E1BEE7', '#7B1FA2', '#BA68C8', '#D500F9'],
  cyan:    ['#00c8ff', '#00e5ff', '#4dd0e1', '#80deea', '#00acc1', '#26c6da', '#b2ebf2'],
  rainbow: ['#ff0000', '#ff7700', '#ffff00', '#00ff00', '#0077ff', '#8800ff', '#ff00ff'],
  champions: ['#4a9eff', '#00c8ff', '#9b6bff', '#c58bff', '#ffd35a', '#ffe680', '#7b5cff'],
};

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  w: number;
  h: number;
  color: string;
  rotation: number;
  rotSpeed: number;
  gravity: number;
  drag: number;
  opacity: number;
  fadeRate: number;
  shape: 'rect' | 'circle' | 'star';
}

/** Shades of one colour, drawn fresh each time: a random hue, then seven
 *  variations of lightness and saturation around it (Ames 2026-10-13: "if
 *  you get blue, lots of blues"). The canvas is in CSS pixels, so a screen
 *  point can be used as is. */
function monoPalette(): string[] {
  const hue = Math.floor(Math.random() * 360);
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    const light = 42 + Math.round((i / 6) * 40);          // 42 to 82
    const sat = 70 + Math.round(Math.random() * 25);       // 70 to 95
    out.push(`hsl(${(hue + Math.round((Math.random() - 0.5) * 16) + 360) % 360}, ${sat}%, ${light}%)`);
  }
  return out;
}

function createParticles(count: number, palette: string[], origin?: { x: number; y: number }) {
  const canvas = canvasRef.value;
  if (!canvas) return;
  // From a given point (a button that was pressed), or the usual place.
  const fromPoint = !!origin && Number.isFinite(origin.x) && Number.isFinite(origin.y);
  const cx = fromPoint ? origin!.x : canvas.width / 2;
  const cy = fromPoint ? origin!.y : canvas.height * 0.35;
  const spreadX = fromPoint ? 24 : 200;
  const spreadY = fromPoint ? 12 : 100;

  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = Math.random() * 12 + 4;
    const shapes: Particle['shape'][] = ['rect', 'rect', 'rect', 'circle', 'star'];
    particles.push({
      x: cx + (Math.random() - 0.5) * spreadX,
      y: cy + (Math.random() - 0.5) * spreadY,
      vx: Math.cos(angle) * speed + (Math.random() - 0.5) * 4,
      vy: Math.sin(angle) * speed - Math.random() * 6 - 2,
      w: Math.random() * 8 + 4,
      h: Math.random() * 6 + 3,
      color: palette[Math.floor(Math.random() * palette.length)],
      rotation: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.3,
      gravity: 0.12 + Math.random() * 0.06,
      drag: 0.98 + Math.random() * 0.015,
      opacity: 1,
      fadeRate: 0.003 + Math.random() * 0.004,
      shape: shapes[Math.floor(Math.random() * shapes.length)],
    });
  }
}

function drawStar(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const angle = (i * 4 * Math.PI) / 5 - Math.PI / 2;
    const method = i === 0 ? 'moveTo' : 'lineTo';
    ctx[method](x + r * Math.cos(angle), y + r * Math.sin(angle));
  }
  ctx.closePath();
  ctx.fill();
}

/* animate() removed — animateSparkles() handles both confetti and sparkles */

function resizeCanvas() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
}

// ── Sparkle mode — calcium-imaging–inspired twinkling stars ─────────────────

interface Sparkle {
  x: number;
  y: number;
  r: number;       // radius
  phase: number;    // twinkle phase offset
  speed: number;    // twinkle speed
  life: number;     // 0→1→0 lifecycle
  lifeSpeed: number;
  color: string;
  maxOpacity: number;
}

const SPARKLE_COLORS = [
  '#00e5ff', '#80deea', '#b2ebf2', '#ffffff', '#CE93D8',
  '#e0f7fa', '#4dd0e1', '#a7ffeb', '#b9f6ca', '#fff9c4',
];

function createSparkles(count: number) {
  const canvas = canvasRef.value;
  if (!canvas) return;
  // Mobile: a brief orienting moment — the whole shimmer lives and dies
  // in about a second while the mind settles into the space; any longer
  // and it blocks the view (Amy 2026-08-24). Desktop keeps the longer
  // ambient wash (up to ~6s).
  const mobile = isMobileRef.value;
  for (let i = 0; i < count; i++) {
    // Stagger spawn times so sparkles appear in waves, not all at once
    const spawnDelay = Math.random() * (mobile ? 0.3 : 0.8);
    sparkles.push({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 3 + 1,            // bigger: 1–4px radius
      phase: Math.random() * Math.PI * 2,
      speed: 0.03 + Math.random() * 0.05,   // faster twinkle
      life: -spawnDelay,                     // negative = waiting to appear
      lifeSpeed: mobile ? 0.046 + Math.random() * 0.018
                        : 0.008 + Math.random() * 0.01,
      color: SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)],
      maxOpacity: 0.7 + Math.random() * 0.3,  // brighter: 0.7–1.0
    });
  }
}

function animateSparkles() {
  if (!ctx || !canvasRef.value) return;
  const canvas = canvasRef.value;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  let alive = false;
  // Dissolving veil: cosine ease from near-opaque to nothing (~1.4s) —
  // smooth at both ends, no jump.
  if (veilT >= 0 && veilT <= 1) {
    const opacity = VEIL_MAX * (0.5 + 0.5 * Math.cos(Math.PI * veilT));
    ctx.save();
    ctx.globalAlpha = 1;
    ctx.fillStyle = `rgba(4, 9, 18, ${opacity})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    veilT += VEIL_STEP;
    alive = true;
  } else {
    veilT = -1;
  }
  for (const s of sparkles) {
    s.life += s.lifeSpeed;
    if (s.life > 2) continue; // dead
    if (s.life < 0) { alive = true; continue; } // waiting to spawn
    alive = true;

    // Bell curve with smoothstep shoulders: eases in and out instead of
    // ramping linearly, so sparkles bloom and dissolve rather than pop.
    const lin = s.life <= 1 ? s.life : 2 - s.life;
    const envelope = lin * lin * (3 - 2 * lin);
    // Twinkle: rapid sin oscillation on top of envelope
    const twinkle = 0.5 + 0.5 * Math.sin(s.phase + s.life * 80 * s.speed);
    const alpha = envelope * twinkle * s.maxOpacity;

    if (alpha <= 0.02) continue;

    ctx.save();
    ctx.globalAlpha = alpha;

    // Outer glow — larger, softer
    ctx.shadowColor = s.color;
    ctx.shadowBlur = s.r * 8;
    ctx.fillStyle = s.color;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
    ctx.fill();

    // Inner glow — second pass for extra brightness
    ctx.shadowBlur = s.r * 3;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.r * 0.7, 0, Math.PI * 2);
    ctx.fill();

    // Bright white center
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#ffffff';
    ctx.globalAlpha = alpha * 0.9;
    ctx.beginPath();
    ctx.arc(s.x, s.y, s.r * 0.35, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // Also render confetti particles if any
  for (const p of particles) {
    if (p.opacity <= 0) continue;
    alive = true;
    p.vy += p.gravity;
    p.vx *= p.drag;
    p.vy *= p.drag;
    p.x += p.vx;
    p.y += p.vy;
    p.rotation += p.rotSpeed;
    if (p.y > canvas.height * 0.5) p.opacity -= p.fadeRate * 2;
    if (p.y > canvas.height + 50) { p.opacity = 0; continue; }
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rotation);
    ctx.globalAlpha = Math.max(0, p.opacity);
    ctx.fillStyle = p.color;
    if (p.shape === 'circle') {
      ctx.beginPath(); ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2); ctx.fill();
    } else if (p.shape === 'star') {
      drawStar(ctx, 0, 0, p.w / 2);
    } else {
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
    }
    ctx.restore();
  }

  if (alive) {
    animFrame = requestAnimationFrame(animateSparkles);
  } else {
    sparkles = [];
    particles = [];
    animFrame = 0;
  }
}

/**
 * Fire a calcium-imaging sparkle shimmer — tiny twinkling stars across the screen.
 * @param intensity - particle count multiplier (1 = subtle, 2 = medium, 3 = dramatic)
 */
function sparkle(intensity: number = 1) {
  // Calm screen: no shimmer of stars across the screen (annkri #144).
  if (isCalm()) return;
  resizeCanvas();
  underUI.value = true;
  // Phones get a thinner field too — density reads as obstruction there.
  if (isMobileRef.value) intensity *= 0.6;
  // Phones: the shimmer is a materialization — start behind a veil that
  // dissolves to reveal the scene. Desktop keeps the plain ambient wash.
  if (isMobileRef.value) veilT = 0;
  const count = Math.round(120 * intensity);
  createSparkles(count);
  if (!animFrame) {
    animFrame = requestAnimationFrame(animateSparkles);
  }
}

/**
 * Fire a confetti burst.
 * @param palette - color palette name: 'default', 'gold', 'purple', 'cyan',
 *   'rainbow', or 'mono' (shades of one colour, a different one each time)
 * @param intensity - particle count multiplier (1 = normal, 2 = big, 3 = epic)
 * @param origin - a point on screen (CSS pixels) to burst from, e.g. the
 *   button that was pressed; the usual place when left out
 */
function trigger(palette: string = 'default', intensity: number = 1, origin?: { x: number; y: number }) {
  resizeCanvas();
  underUI.value = false;
  veilT = -1;
  const colors = palette === 'mono' ? monoPalette() : (PALETTES[palette] || PALETTES.default);
  const count = Math.round(80 * intensity);
  createParticles(count, colors, origin);
  if (!animFrame) {
    animFrame = requestAnimationFrame(animateSparkles);
  }
}

onMounted(() => {
  const canvas = canvasRef.value;
  if (canvas) {
    ctx = canvas.getContext('2d');
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
  }
});

onUnmounted(() => {
  cancelAnimationFrame(animFrame);
  window.removeEventListener('resize', resizeCanvas);
});

defineExpose({ trigger, sparkle });
</script>

<template>
  <Teleport to="body" :disabled="props.inline">
    <canvas
      ref="canvasRef"
      class="nge-confetti-canvas"
      :class="{ 'nge-confetti-canvas--under': underUI, 'nge-confetti-canvas--inline': props.inline }"
    />
  </Teleport>
</template>

<style scoped>
.nge-confetti-canvas {
  position: fixed;
  inset: 0;
  z-index: 99999;
  pointer-events: none;
  width: 100vw;
  height: 100vh;
}
/* Sparkle-only runs: below the mobile welcome sheet (10500), above the rest. */
.nge-confetti-canvas--under { z-index: 10400; }
/* Inline: the bottom layer of whatever holds it. */
.nge-confetti-canvas--inline { z-index: 0; }
</style>
